/**
 * Heinrich 블로그 — 목록(인덱스) 페이지.
 * 다크 강제 + URL 기반 언어(/blog=ko, /en/blog=en) + 사이트 팔레트(웜 zinc + 골드).
 * 정갈한 에디토리얼 레이아웃: 컴팩트 히어로 → 최신 글(featured) → 정돈된 목록.
 */
import { Helmet } from 'react-helmet-async'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { FiArrowRight } from 'react-icons/fi'
import ForceDarkTheme from '../../components/common/ForceDarkTheme'
import ForceLocale from '../../components/common/ForceLocale'
import { getPosts, BLOG_UI, type BlogPost } from './blogData'
import { fetchDbPosts } from './blogApi'

// DB(API) 글 + TS 번들 글 병합 — 같은 slug 는 DB 우선, date 내림차순.
function mergePosts(dbPosts: BlogPost[], tsPosts: BlogPost[]): BlogPost[] {
  const bySlug = new Map<string, BlogPost>()
  tsPosts.forEach((p) => bySlug.set(p.slug, p))
  dbPosts.forEach((p) => bySlug.set(p.slug, p))  // DB 우선
  return [...bySlug.values()].sort((a, b) => (b.date || '').localeCompare(a.date || ''))
}

export default function Blog() {
  const { pathname } = useLocation()
  const lang: 'ko' | 'en' = pathname.startsWith('/en/') ? 'en' : 'ko'
  const base = lang === 'en' ? '/en/blog' : '/blog'
  const s = BLOG_UI[lang]

  const { data: dbPosts = [] } = useQuery({
    queryKey: ['blog-list', lang],
    queryFn: () => fetchDbPosts(lang),
    staleTime: 60_000,
  })

  const posts = mergePosts(dbPosts, getPosts(lang))
  const featured = posts.find((p) => p.featured) ?? posts[0]
  const rest = posts.filter((p) => p.slug !== featured?.slug)

  const canonical = `https://heinrich.dasancms.co.kr${base}/`

  return (
    <>
      <ForceDarkTheme />
      <ForceLocale lang={lang} />
      <Helmet>
        <html lang={lang} />
        <title>{s.metaTitle}</title>
        <meta name="description" content={s.metaDesc} />
        <meta name="keywords" content={
          lang === 'ko'
            ? '산업안전,위험성평가,JSA,TBM,작업허가서,중대재해처벌법,KOSHA,안전관리,하인리히,Heinrich,안전AI'
            : 'industrial safety,risk assessment,JSA,toolbox meeting,permit to work,KOSHA,EHS,safety management,Heinrich,safety AI'
        } />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="ko" href="https://heinrich.dasancms.co.kr/blog/" />
        <link rel="alternate" hrefLang="en" href="https://heinrich.dasancms.co.kr/en/blog/" />
        <link rel="alternate" hrefLang="x-default" href="https://heinrich.dasancms.co.kr/blog/" />
        <meta property="og:site_name" content="Heinrich" />
        <meta property="og:title" content={s.metaTitle} />
        <meta property="og:description" content={s.metaDesc} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        {featured && <meta property="og:image" content={`https://heinrich.dasancms.co.kr${featured.cover}`} />}
        {featured && <meta property="og:image:alt" content={featured.title} />}
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={s.metaTitle} />
        <meta name="twitter:description" content={s.metaDesc} />
        {featured && <meta name="twitter:image" content={`https://heinrich.dasancms.co.kr${featured.cover}`} />}
      </Helmet>

      {/* ── 히어로 ── */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-mesh" />
        <div className="relative mx-auto max-w-3xl px-6 pt-20 pb-14 text-center">
          <span className="text-[12px] font-semibold uppercase tracking-[0.18em] text-brand-primary-light">
            {s.eyebrow}
          </span>
          <h1 className="mx-auto mt-6 max-w-[20ch] text-[34px] font-extrabold leading-[1.1] tracking-tight text-ink sm:text-[46px]">
            {s.heroTitle}
          </h1>
          <p className="mx-auto mt-6 max-w-[56ch] text-[17px] leading-relaxed text-ink-muted">
            {s.heroSub}
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-6 pb-24">
        <div className="h-px w-full bg-surface-border/40" />

        {/* ── 최신 글 ── */}
        {featured && (
          <section className="pt-12">
            <div className="mb-6 text-[12px] font-semibold uppercase tracking-[0.16em] text-ink-faint">
              {s.latest}
            </div>
            <Link
              to={`${base}/${featured.slug}`}
              className="group grid gap-8 md:grid-cols-[1.1fr_1fr] md:items-center"
            >
              <div className="overflow-hidden rounded-2xl border border-surface-border/50">
                <img
                  src={featured.cover} alt=""
                  className="aspect-[16/10] w-full object-cover transition duration-300 group-hover:scale-[1.02]"
                />
              </div>
              <div>
                <div className="flex items-center gap-2.5 text-[12.5px] text-ink-faint">
                  <span className="font-semibold uppercase tracking-wide text-brand-primary-light">{featured.category}</span>
                  {!featured.hideDate && (<><span aria-hidden>·</span><span>{featured.date}</span></>)}
                  <span aria-hidden>·</span><span>{s.min(featured.readMin)}</span>
                </div>
                <h2 className="mt-3 text-[27px] font-extrabold leading-[1.15] tracking-tight text-ink">
                  {featured.title}
                </h2>
                <p className="mt-3.5 text-[16px] leading-relaxed text-ink-muted">{featured.standfirst}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-[14.5px] font-semibold text-brand-primary-light">
                  {s.readMore} <FiArrowRight className="transition group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </section>
        )}

        {/* ── 목록 ── */}
        {rest.length > 0 && (
          <section className="mt-14 border-t border-surface-border/40 pt-12">
            <div className="grid gap-x-8 gap-y-10 sm:grid-cols-2">
              {rest.map((p) => <PostRow key={p.slug} p={p} base={base} s={s} />)}
            </div>
          </section>
        )}
      </div>
    </>
  )
}

function PostRow({ p, base, s }: { p: BlogPost; base: string; s: typeof BLOG_UI['ko'] | typeof BLOG_UI['en'] }) {
  return (
    <Link to={`${base}/${p.slug}`} className="group flex flex-col">
      <div className="overflow-hidden rounded-xl border border-surface-border/50">
        <img src={p.cover} alt="" className="aspect-[16/10] w-full object-cover transition duration-300 group-hover:scale-[1.02]" />
      </div>
      <div className="mt-4 flex items-center gap-2.5 text-[12px] text-ink-faint">
        <span className="font-semibold uppercase tracking-wide text-brand-primary-light">{p.category}</span>
        {!p.hideDate && (<><span aria-hidden>·</span><span>{p.date}</span></>)}
      </div>
      <h3 className="mt-2 text-[19px] font-bold leading-snug tracking-tight text-ink">{p.title}</h3>
      <p className="mt-2 text-[15px] leading-relaxed text-ink-muted line-clamp-2">{p.standfirst}</p>
    </Link>
  )
}
